import { useCallback, useState } from 'react';
import type { FileContext, GpsInput } from '@eombor/contracts';
import type { HandoverPhotoDraft } from './handover.store.js';
import { useUploadPhoto } from './handover.queries.js';

/**
 * BIR MARTALIK foto-hisobot qoralamasi — bitta oyna umri davomida.
 *
 * `handover.store` (zustand) dan farqi maqsadda: u ko'p bosqichli berish
 * oqimini olib yuradi va bosqichlar orasida SAQLANIB qolishi kerak. Qabul
 * oynasi esa ochiladi, bir ish qiladi va yopiladi — uning holati global
 * do'konda yashasa, oyna yopilganda qo'lda tozalash kerak bo'lardi va
 * unutilgan bitta tozalash keyingi talabnomaga begona rasm olib kirardi.
 *
 * Rasm tanlangan zahoti YUKLANADI: bir necha megabaytli faylni oxirgi
 * tugmaga saqlab qo'yish "Tasdiqlash" ni bir necha soniyaga cho'zardi,
 * odam esa tugmani bosgach darhol javob kutadi.
 */

/**
 * Qoralama rasm + uning O'Z koordinatasi.
 *
 * Koordinata serverdan qaytadi (`StoredFileDto.gps`): u rasmning EXIF idan
 * o'qiladi va bu `http://` bilan ishlaydigan ichki tarmoqda YAGONA
 * ishlaydigan manba — brauzerning joylashuv xizmati u yerda bloklangan.
 *
 * Ekran uni bilishi kerak, chunki qabul koordinatasiz qabul qilinmaydi:
 * sabab tugma bosilgandan KEYIN emas, oldindan aytilishi kerak.
 */
export interface PhotoDraft extends HandoverPhotoDraft {
  gps: GpsInput | null;
}

export interface PhotoDrafts {
  photos: readonly PhotoDraft[];
  /** Serverga yetib borgan rasmlar — dalolatnomaga aynan shular biriktiriladi. */
  readyIds: string[];
  /** Kamida bittasi hali yuklanmoqda — tasdiq tugmasi kutishi kerak. */
  uploading: boolean;
  /** Yuklangan rasmlardan birortasida koordinata bormi. */
  gps: GpsInput | null;
  pick: (files: File[]) => void;
  remove: (id: string) => void;
  reset: () => void;
}

let counter = 0;
const nextId = (): string => `photo-${(counter += 1)}`;

export const usePhotoDrafts = (context: FileContext): PhotoDrafts => {
  const [photos, setPhotos] = useState<PhotoDraft[]>([]);
  const upload = useUploadPhoto();

  /*
   * Oldindan ko'rish manzillari `URL.createObjectURL` bilan yasaladi va
   * brauzer ularni o'zi tozalamaydi — shuning uchun rasm ro'yxatdan
   * chiqqanda BIRMA-BIR bekor qilinadi (`remove`, `reset`).
   *
   * UNMOUNT da "hammasini bekor qil" degan tozalash BO'LMASLIGI kerak va
   * buning sababi aniq: React `StrictMode` da har effektni bir marta
   * soxta unmount bilan qaytaradi, holat esa saqlanib qoladi. O'sha
   * tozalash tirik havolani o'chirib, oynada fayl NOMI turgan, rasmning
   * o'zi esa ochilmaydigan buzuq eskiz qoldirardi — aynan foydalanuvchi
   * ko'rgan hol. Yopish va tasdiqlash yo'llarining ikkalasi ham `reset`
   * dan o'tadi, ya'ni amaldagi har oqim havolani baribir bo'shatadi.
   */
  const revoke = (url: string): void => {
    URL.revokeObjectURL(url);
  };

  const pick = useCallback(
    (files: File[]) => {
      for (const file of files) {
        const id = nextId();
        const previewUrl = URL.createObjectURL(file);

        // Ko'rish YUKLASHNI KUTMAYDI: olgan suratini darhol ko'rmagan odam
        // uni ikkinchi marta oladi.
        setPhotos((current) => [
          ...current,
          { id, name: file.name, previewUrl, fileId: null, status: 'uploading', gps: null },
        ]);

        /*
         * Har fayl uchun ALOHIDA va'da (`mutateAsync`), `mutate` ning
         * chaqiruv ilmoqlari emas: ikkinchi `mutate` birinchisining
         * `onSuccess` ini almashtirib yuboradi va birinchi rasm abadiy
         * "yuklanmoqda" bo'lib qolardi.
         */
        void upload
          .mutateAsync({ file, context })
          .then((stored) => {
            setPhotos((current) =>
              current.map((photo) =>
                photo.id === id
                  ? { ...photo, status: 'ready', fileId: stored.id, gps: stored.gps }
                  : photo,
              ),
            );
          })
          .catch(() => {
            setPhotos((current) =>
              current.map((photo) => (photo.id === id ? { ...photo, status: 'failed' } : photo)),
            );
          });
      }
    },
    [context, upload],
  );

  const remove = useCallback((id: string) => {
    setPhotos((current) => {
      const found = current.find((photo) => photo.id === id);
      if (found !== undefined) revoke(found.previewUrl);
      return current.filter((photo) => photo.id !== id);
    });
  }, []);

  const reset = useCallback(() => {
    setPhotos((current) => {
      for (const photo of current) revoke(photo.previewUrl);
      return [];
    });
  }, []);

  return {
    photos,
    readyIds: photos.flatMap((photo) => (photo.fileId === null ? [] : [photo.fileId])),
    uploading: photos.some((photo) => photo.status === 'uploading'),
    /* BIRINCHI koordinatali rasm: server ham aynan shu qoidani ishlatadi
       (`coordinateOf`), ya'ni ekran boshqa rasmni ko'rsatib qo'ymaydi. */
    gps: photos.find((photo) => photo.gps !== null)?.gps ?? null,
    pick,
    remove,
    reset,
  };
};
